import React, { useState, useEffect, useContext, useCallback } from "react";
import { CircularProgress } from "@mui/material";
import { ChartLineUpIcon, ArrowsClockwiseIcon, CaretLeft, CaretRight } from "@phosphor-icons/react";
import { AuthContext } from "../../contexts";
import { CustomPageHeader, CustomTag } from "../../ui";
import axios from "axios";
import "../../assets/css/pages/tracking/weekly-overview.css";

const WeeklyOverview = () => {
    const { token } = useContext(AuthContext);
    const [weeklyData, setWeeklyData] = useState({ year: new Date().getFullYear(), users: [] });
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [isLoading, setIsLoading] = useState(false);

    const fetchWeeklyData = useCallback(async (year) => {
        setIsLoading(true);
        try {
            const response = await axios.get(`${process.env.REACT_APP_UPA_API_HOST}dashboard/weekly-entries/${year}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setWeeklyData(response.data);
        } catch (error) {
            console.error("Erreur lors du chargement des données hebdomadaires:", error);
            setWeeklyData({ year, users: [] });
        } finally {
            setIsLoading(false);
        }
    }, [token]);

    useEffect(() => {
        fetchWeeklyData(selectedYear);
    }, [selectedYear, fetchWeeklyData]);

    const getWeekDates = (year, weekNum) => {
        const jan4 = new Date(year, 0, 4);
        const dayOfWeek = jan4.getDay() || 7;
        const firstMonday = new Date(jan4);
        firstMonday.setDate(jan4.getDate() - dayOfWeek + 1);

        const weekStart = new Date(firstMonday);
        weekStart.setDate(firstMonday.getDate() + (weekNum - 1) * 7);

        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 4);

        const formatDate = (date) => {
            const day = String(date.getDate()).padStart(2, "0");
            const month = String(date.getMonth() + 1).padStart(2, "0");
            return `${day}/${month}`;
        };

        return `${formatDate(weekStart)} - ${formatDate(weekEnd)}`;
    };

    const users = weeklyData.users || [];

    return (
        <div className="c-app__view">
            <CustomPageHeader
                title="Suivi équipe"
                titleIcon={{ icon: ChartLineUpIcon }}
                buttons={[
                    {
                        label: "",
                        onClick: () => setSelectedYear((prev) => prev - 1),
                        icon: CaretLeft,
                        variant: "secondary",
                        className: "gap-0",
                    },
                    {
                        label: String(selectedYear),
                        onClick: () => {},
                        variant: "secondary",
                        className: "year-display",
                        disabled: true,
                    },
                    {
                        label: "",
                        onClick: () => setSelectedYear((prev) => prev + 1),
                        icon: CaretRight,
                        variant: "secondary",
                        className: "gap-0",
                        disabled: selectedYear >= new Date().getFullYear(),
                    },
                    {
                        label: isLoading ? "Chargement..." : "Rafraîchir",
                        onClick: () => fetchWeeklyData(selectedYear),
                        icon: isLoading ? null : ArrowsClockwiseIcon,
                        disabled: isLoading,
                        loading: isLoading,
                        variant: "primary",
                    },
                ]}
            />

            <div className="tabs-container">
                <div className="weekly-overview-card">
                    {isLoading ? (
                        <div className="loader-container">
                            <CircularProgress size={30} />
                        </div>
                    ) : users.length === 0 ? (
                        <div className="weekly-no-data">Aucune donnée disponible pour {selectedYear}</div>
                    ) : (
                        <div className="weekly-table-wrapper">
                            <table className="weekly-table">
                                <thead>
                                    <tr>
                                        <th className="weekly-table__header-cell weekly-table__header-cell--sticky">Semaine</th>
                                        <th className="weekly-table__header-cell weekly-table__header-cell--period">Période</th>
                                        {users.map((user) => (
                                            <th key={user.id} className="weekly-table__header-cell">
                                                {user.firstName} {user.lastName.charAt(0)}.
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {Array.from({ length: 52 }, (_, i) => i + 1).map((weekNum) => (
                                        <tr key={weekNum} className="weekly-table__row">
                                            <td className="weekly-table__cell weekly-table__cell--sticky">S{weekNum}</td>
                                            <td className="weekly-table__cell weekly-table__cell--period">{getWeekDates(selectedYear, weekNum)}</td>
                                            {users.map((user) => {
                                                const weekData = user.weeks?.find((w) => w.week === weekNum);
                                                const duration = weekData?.totalDuration || 0;
                                                return (
                                                    <td key={user.id} className="weekly-table__cell">
                                                        {duration > 0 ? <CustomTag value={duration.toFixed(1)} /> : "-"}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default WeeklyOverview;
